import { useEffect, useMemo, useState } from "react";
import { Appointments, Patients } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday = 0
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - day);
  return x;
}
const HOURS = Array.from({ length: 12 }, (_, i) => 8 + i); // 08:00 - 19:00
const DAY_NAMES = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export default function CalendarPage() {
  const [view, setView] = useState("week"); // week | day
  const [anchor, setAnchor] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", scheduled_at: "", notes: "" });

  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  }), [weekStart]);

  const load = async () => {
    const lo = new Date(weekStart); lo.setDate(lo.getDate() - 1);
    const hi = new Date(weekStart); hi.setDate(hi.getDate() + 8);
    const [list, ps] = await Promise.all([
      Appointments.list({ start: lo.toISOString(), end: hi.toISOString() }),
      Patients.list(),
    ]);
    setAppts(list);
    setPatients(ps);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anchor]);

  const byDayHour = useMemo(() => {
    const m = {};
    appts.forEach((a) => {
      if (!a.scheduled_at) return;  // sessões ainda por agendar não entram na grelha
      const d = new Date(a.scheduled_at);
      const key = `${d.toDateString()}-${d.getHours()}`;
      (m[key] = m[key] || []).push(a);
    });
    return m;
  }, [appts]);

  const shift = (delta) => {
    const d = new Date(anchor);
    if (view === "week") d.setDate(d.getDate() + delta * 7);
    else d.setDate(d.getDate() + delta);
    setAnchor(d);
  };

  const create = async () => {
    if (!form.patient_id || !form.scheduled_at) { toast.error("Preenche paciente e data/hora"); return; }
    try {
      await Appointments.create({
        patient_id: form.patient_id,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        notes: form.notes || "",
      });
      toast.success("Marcação criada");
      setOpen(false);
      setForm({ patient_id: "", scheduled_at: "", notes: "" });
      load();
    } catch { toast.error("Falha ao criar marcação"); }
  };

  const removeAppt = async (id) => {
    if (!window.confirm("Eliminar esta marcação?")) return;
    await Appointments.remove(id);
    toast.success("Eliminada");
    load();
  };

  const activeDays = view === "week" ? days : [anchor];

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="calendar-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Agenda</div>
          <h1 className="font-display text-4xl text-slate-50 mt-2">Calendário</h1>
          <p className="text-slate-400 mt-1 text-sm">Gere as sessões e integra com o plano de tratamento.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => shift(-1)} className="rounded-full border-slate-800 text-slate-300" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => setAnchor(new Date())} className="rounded-full border-slate-800 text-slate-300" data-testid="cal-today">Hoje</Button>
          <Button variant="outline" onClick={() => shift(1)} className="rounded-full border-slate-800 text-slate-300" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          <div className="ml-4 flex bg-slate-900/60 border border-slate-800 rounded-full p-1">
            <button data-testid="view-week" onClick={() => setView("week")} className={`px-3 py-1 rounded-full text-sm ${view === "week" ? "bg-sky-500 text-slate-950" : "text-slate-400"}`}>Semana</button>
            <button data-testid="view-day" onClick={() => setView("day")} className={`px-3 py-1 rounded-full text-sm ${view === "day" ? "bg-sky-500 text-slate-950" : "text-slate-400"}`}>Dia</button>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-appointment-btn" className="rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 glow-sky">
                <Plus className="w-4 h-4 mr-2" /> Nova marcação
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-950 border-slate-800 text-slate-100">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Nova marcação</DialogTitle>
                <DialogDescription className="text-slate-400">Agenda uma sessão para um paciente.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div>
                  <Label className="text-slate-300">Paciente</Label>
                  <Select value={form.patient_id} onValueChange={(v) => setForm({...form, patient_id: v})}>
                    <SelectTrigger className="bg-slate-900 border-slate-800 text-slate-50" data-testid="select-patient">
                      <SelectValue placeholder="Escolher paciente" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                      {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Data & hora</Label>
                  <Input type="datetime-local" data-testid="input-datetime" value={form.scheduled_at} onChange={(e) => setForm({...form, scheduled_at: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
                </div>
                <div>
                  <Label className="text-slate-300">Notas</Label>
                  <Input data-testid="input-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Cancelar</Button>
                <Button data-testid="create-appointment-submit" onClick={create} className="rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 px-5">Criar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        <div className="grid" style={{ gridTemplateColumns: `80px repeat(${activeDays.length}, minmax(0, 1fr))` }}>
          <div className="bg-slate-950/50 border-b border-slate-800"></div>
          {activeDays.map((d, i) => (
            <div key={i} className="bg-slate-950/50 border-b border-l border-slate-800 px-3 py-2 text-center">
              <div className="text-xs text-slate-500 uppercase tracking-wide">{DAY_NAMES[(d.getDay()+6)%7]}</div>
              <div className={`font-display text-xl ${d.toDateString()===new Date().toDateString() ? "text-sky-300" : "text-slate-100"}`}>{d.getDate()}</div>
            </div>
          ))}
          {HOURS.map((h) => (
            <div key={h} className="contents">
              <div className="text-xs text-slate-500 px-2 py-2 border-b border-slate-800 font-mono">{String(h).padStart(2,"0")}:00</div>
              {activeDays.map((d, i) => {
                const key = `${d.toDateString()}-${h}`;
                const events = byDayHour[key] || [];
                return (
                  <div key={i} className="min-h-[64px] border-b border-l border-slate-800 p-1 space-y-1">
                    {events.map((e) => (
                      <div key={e.id} data-testid={`appt-${e.id}`} className="group rounded-lg border border-sky-500/40 bg-sky-500/10 text-sky-100 text-xs p-2">
                        <div className="flex justify-between">
                          <span className="font-medium">{e.patient_name || "Paciente"}</span>
                          <button className="opacity-0 group-hover:opacity-100 text-rose-300" onClick={() => removeAppt(e.id)}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="text-[10px] text-sky-300/80 mt-0.5">
                          {new Date(e.scheduled_at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                          {e.session_number && ` · Sessão ${e.session_number}`}
                        </div>
                        {e.notes && <div className="text-[10px] text-slate-400 mt-1 line-clamp-1">{e.notes}</div>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
